import { type JSX, useState } from 'react';

import { gutscheinBetragSetzen } from '../api/produkte';
import { ApiFehler } from '../api/fehler';
import { Abschnitt } from '../komponenten/Akte';

/**
 * Der Wert eines Gutscheinartikels (C18, GUT-05; 0274).
 *
 * FEST ODER FREI: Ein Gutschein zu 50 € hat einen festen Betrag; ein
 * Wertgutschein nach Wahl einen Rahmen, in dem der Kunde im Portal waehlt.
 * Beides zugleich nimmt der Server nicht an. Buchbar wird der Artikel erst,
 * wenn eines davon steht -- die Datenbank weist es sonst ab.
 */
export function Gutscheinbetrag({
  artikel,
  angaben,
  darfAendern,
  beiGesichert,
  beiFehler,
}: {
  readonly artikel: string;
  readonly angaben: { readonly art: string | null; readonly betrag: string | null; readonly von: string | null; readonly bis: string | null };
  readonly darfAendern: boolean;
  readonly beiGesichert: () => void;
  readonly beiFehler: (fehler: ApiFehler) => void;
}): JSX.Element {
  const [frei, setzeFrei] = useState(angaben.betrag === null);
  const [betrag, setzeBetrag] = useState(angaben.betrag ?? '');
  const [von, setzeVon] = useState(angaben.von ?? '');
  const [bis, setzeBis] = useState(angaben.bis ?? '');
  const [laeuft, setzeLaeuft] = useState(false);

  const sichern = async (): Promise<void> => {
    setzeLaeuft(true);
    try {
      await gutscheinBetragSetzen(artikel, {
        art: angaben.art ?? 'wert',
        betrag: frei ? null : betrag.trim(),
        von: frei ? von.trim() : null,
        bis: frei ? bis.trim() : null,
      });
      beiGesichert();
    } catch (f) {
      if (!(f instanceof ApiFehler)) throw f;
      beiFehler(f);
    } finally {
      setzeLaeuft(false);
    }
  };

  /* ÄA-41: ein aufklappbarer Abschnitt in den Stammdaten der Artikelakte. */
  return (
    <Abschnitt titel="Wert des Gutscheins">
      <div className="umschalter" role="group" aria-label="Art des Werts">
        {[
          { wert: false, wort: 'fester Betrag' },
          { wert: true, wort: 'Kunde wählt im Rahmen' },
        ].map((s) => (
          <button
            key={s.wort}
            type="button"
            disabled={!darfAendern}
            className={`umschalter__wahl${frei === s.wert ? ' reiter__wahl--hier' : ''}`}
            aria-pressed={frei === s.wert}
            onClick={() => setzeFrei(s.wert)}
          >
            {s.wort}
          </button>
        ))}
      </div>
      {frei ? (
        <>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="gs-von">Mindestwert (€)</label>
            <input id="gs-von" inputMode="decimal" disabled={!darfAendern} value={von} onChange={(e) => setzeVon(e.target.value)} />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="gs-bis">Höchstwert (€)</label>
            <input id="gs-bis" inputMode="decimal" disabled={!darfAendern} value={bis} onChange={(e) => setzeBis(e.target.value)} />
          </div>
        </>
      ) : (
        <div className="feld">
          <label className="feld__beschriftung" htmlFor="gs-betrag">Betrag (€)</label>
          <input id="gs-betrag" inputMode="decimal" disabled={!darfAendern} value={betrag} onChange={(e) => setzeBetrag(e.target.value)} />
        </div>
      )}
      <p className="feld__hilfe">
        Der Verkauf eines Wertgutscheins ist kein steuerbarer Umsatz; die Steuer entsteht bei der Einlösung.
      </p>
      {darfAendern && (
        <div className="knopfreihe">
          <button type="button" className="knopf knopf--klein" disabled={laeuft} onClick={() => void sichern()}>
            Wert sichern
          </button>
        </div>
      )}
    </Abschnitt>
  );
}
